import { useSearchParams } from 'react-router';
import { useDashboardAssignments, useDashboardStudents } from '@/hooks/use-dashboard';
import { AssignmentsTable } from '@/components/assignments/assignments-table';
import { AssignmentFilters } from '@/components/assignments/assignment-filters';
import { CreateAssignmentDialog } from '@/components/assignments/create-assignment-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export default function AssignmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const perPage = Number(searchParams.get('per_page') ?? '20');
  const status = searchParams.get('status') ?? 'all';
  const studentId = searchParams.get('student_id') ?? 'all';
  const sortOrder = searchParams.get('sort_order') ?? 'desc';

  const { data, isLoading } = useDashboardAssignments({
    page,
    per_page: perPage,
    status: status === 'all' ? undefined : status,
    student_id: studentId === 'all' ? undefined : studentId,
    sort_order: sortOrder,
  });

  const { data: studentsData } = useDashboardStudents({ per_page: 100 });

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <AssignmentFilters
          status={status}
          studentId={studentId}
          sortOrder={sortOrder}
          students={studentsData?.students}
          onStatusChange={(v) => v && updateParam('status', v)}
          onStudentChange={(v) => v && updateParam('student_id', v)}
          onSortOrderToggle={() => updateParam('sort_order', sortOrder === 'asc' ? 'desc' : 'asc')}
        />
        <CreateAssignmentDialog />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <AssignmentsTable assignments={data?.assignments ?? []} />
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParam('page', String(Math.max(1, page - 1)))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => updateParam('page', String(p))}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updateParam('page', String(Math.min(totalPages, page + 1)))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
